import { type FC, type ReactNode } from "react";
import cn from "classnames";

import CardThumbnail from "./cardThumbnail/CardThumbnail";
import CardContent from "./cardContent/CardContent";
import { PublishStatus } from "components/common/PublishStatus";
import { PublishedStatusTypes } from "../../enums";
import EllipsisPopupButton from "../buttons/ellipsisPopupButton/EllipsisPopupButton";
import SelectableCard from "./selectableCard/SelectableCard";
import RestrictedByTooltip from "../restrictedByTooltip/RestrictedByTooltip";

import "./publishStatusCard.scss";

type IconProps = {
  iconClassName: string;
  iconSize?: string;
  tooltipContent?: string | ReactNode;
  tooltipPosition?: string;
  tooltipSize?: string;
  tooltipClassName?: string;
};

export type Props = {
  id: number;
  thumbnailUrl?: string | null;
  title?: string;
  publisher?: string;
  published?: boolean;
  selected?: boolean;
  disabled?: boolean;
  isRadioButton?: boolean;
  onTitleClick?: (id: number) => void;
  onSelectionChanged?: (id: number) => void;
  popupMenuButtons?: ReactNode[] | null;
  popupMenu?: ReactNode;
  disablePopupMenu?: boolean;
  className?: string;
  children?: ReactNode;
  hasBeenPublished?: boolean;
  thumbnailContent?: any;
  icons?: (IconProps | null)[];
  hasEditPermission?: boolean;
  renderMeta?: (renderPopupMenu: () => ReactNode) => ReactNode;
  url?: string;
  placeholderContent?: ReactNode;
  selectable?: boolean;
  isHidden?: boolean;
};

const PublishStatusCard: FC<Props> = ({
  url,
  id,
  thumbnailUrl,
  placeholderContent,
  title,
  publisher,
  published,
  children,
  selected,
  onTitleClick,
  onSelectionChanged,
  popupMenuButtons,
  popupMenu,
  className,
  icons,
  disabled,
  hasEditPermission,
  disablePopupMenu,
  renderMeta,
  isRadioButton,
  selectable,
  hasBeenPublished,
  thumbnailContent,
  isHidden,
}) => {
  const hasPermission = hasEditPermission ?? true;

  const renderPopupMenu = () => {
    if (popupMenu) return popupMenu;

    return (
      popupMenuButtons && (
        <RestrictedByTooltip hasPermission={hasPermission}>
          <EllipsisPopupButton circle disabled={disabled || disablePopupMenu}>
            {popupMenuButtons}
          </EllipsisPopupButton>
        </RestrictedByTooltip>
      )
    );
  };

  const renderCardBottom = () => {
    if (renderMeta) {
      return renderMeta(renderPopupMenu);
    }

    return (
      <div className="bottom-row">
        <PublishStatus
          publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(published, hasBeenPublished)}
        />
        <div onClick={(e) => e.preventDefault()}>{renderPopupMenu()}</div>
      </div>
    );
  };

  return (
    <SelectableCard
      url={url}
      onCardClick={!url ? onTitleClick : undefined}
      id={id}
      className={cn("publish-status-card", className)}
      selected={selected}
      onSelect={onSelectionChanged}
      disabled={disabled}
    >
      <CardThumbnail
        disabled={disabled}
        thumbnailUrl={thumbnailUrl}
        selected={selected}
        hasEditPermission={hasPermission}
        placeholderContent={placeholderContent}
        isRadioButton={isRadioButton}
        selectable={selectable}
        isHidden={isHidden}
      >
        {thumbnailContent}
      </CardThumbnail>
      <CardContent title={title} meta={publisher} icons={icons}>
        <div className="top-row">{children}</div>
        {renderCardBottom()}
      </CardContent>
    </SelectableCard>
  );
};

export default PublishStatusCard;
